import { FC, useEffect, useRef, useState } from 'react'

import { Box, BoxProps, Flex, Image, Text, useMediaQuery } from '@chakra-ui/react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useReadLocalStorage } from 'usehooks-ts'

import { MainButton } from '@core/components/MainButton'
import { MainIconButton } from '@core/components/MainIconButton'
import { NavItem } from '@core/components/Sidebar/NavItem'
import { useFeedbackModal } from '@core/utils/contexts/FeedbackModalContext'

import { IconClose, IconFeedback } from '@page/absence/assets/icons'

import ROCKET_IMAGE from '../assets/images/rocket.svg'

interface FeedbackPopoverProps extends BoxProps {
    onCloseSidebar?: () => void
}

const POPOVER_WIDTH = 240
const SIDEBAR_WIDTH_CLOSED = 56
const SIDEBAR_WIDTH_OPEN = 240
const BOTTOM_OFFSET = 20
const SIDEBAR_GAP = 5

const ANIMATION_DURATION = 200

export const FeedbackPopover: FC<FeedbackPopoverProps> = ({ onCloseSidebar, ...props }) => {
    const { t } = useTranslation()
    const { open } = useFeedbackModal()
    const [isOpen, setIsOpen] = useState(false)
    const [isVisible, setIsVisible] = useState(false)
    const [isMounted, setIsMounted] = useState(false)
    const [isImageReady, setIsImageReady] = useState(false)
    const isSidebarOpened = useReadLocalStorage('isSidebarOpened') as boolean | null
    const [isMobile] = useMediaQuery('(max-width: 768px)', { fallback: false })
    const popoverRef = useRef<HTMLDivElement>(null)

    const showLabel = isSidebarOpened || isMobile
    const sidebarWidth = showLabel ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED
    const popoverLeft = sidebarWidth + SIDEBAR_GAP

    const handleTriggerClick = () => {
        if (isMobile) {
            onCloseSidebar?.()
            open()
            return
        }
        setIsOpen((prev) => !prev)
    }

    useEffect(() => {
        const img = new window.Image()
        img.src = ROCKET_IMAGE
        if (img.complete) {
            setIsImageReady(true)
        } else {
            img.onload = () => setIsImageReady(true)
        }
    }, [])

    useEffect(() => {
        if (isOpen) {
            setIsMounted(true)
        } else {
            setIsVisible(false)
            const timer = setTimeout(() => setIsMounted(false), ANIMATION_DURATION)
            return () => clearTimeout(timer)
        }
    }, [isOpen])

    useEffect(() => {
        if (isMounted && isOpen && isImageReady) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsVisible(true)
                })
            })
        }
    }, [isMounted, isOpen, isImageReady])

    const triggerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                popoverRef.current &&
                !popoverRef.current.contains(target) &&
                triggerRef.current &&
                !triggerRef.current.contains(target)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    return (
        <Box {...props}>
            <Box ref={triggerRef} display={'inline-block'} w={'full'}>
                {showLabel ? (
                    <NavItem
                        icon={<IconFeedback color={isOpen ? '#1D48C0' : '#0C0C0D'} />}
                        hoveredIcon={<IconFeedback color={isOpen ? '#1D48C0' : '#0C0C0D'} />}
                        label={t('9477', 'Submit feedback') || 'Submit feedback'}
                        active={isOpen}
                        sidebarClosed={!showLabel}
                        onClick={handleTriggerClick}
                    />
                ) : (
                    <MainIconButton
                        type="button"
                        icon={<IconFeedback color={isOpen ? '#FFF' : '#0C0C0D'} />}
                        view={'ghost'}
                        size={'md'}
                        aria-label={'Feedback'}
                        mt={'auto'}
                        borderRadius={'full'}
                        bg={isOpen ? '#1D48C0 !important' : 'transparent'}
                        onClick={handleTriggerClick}
                    />
                )}
            </Box>
            {isMounted &&
                createPortal(
                    <Box
                        ref={popoverRef}
                        position="fixed"
                        zIndex={9999}
                        bottom={`${BOTTOM_OFFSET}px`}
                        left={`${popoverLeft}px`}
                        w={`${POPOVER_WIDTH}px`}
                        bg="white"
                        border={'1px solid #245AF0'}
                        boxShadow={'0px 8px 16px 0px #0000000F'}
                        borderRadius={'8px'}
                        p={'20px'}
                        opacity={isVisible ? 1 : 0}
                        transform={isVisible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(8px)'}
                        transition={`opacity ${ANIMATION_DURATION}ms ease-out, transform ${ANIMATION_DURATION}ms ease-out`}
                    >
                        <MainIconButton
                            position={'absolute'}
                            right={'16px'}
                            top={'16px'}
                            type={'button'}
                            icon={<IconClose />}
                            view={'ghost'}
                            size={'md'}
                            aria-label={'Close'}
                            onClick={() => setIsOpen(false)}
                        />
                        <Flex
                            flexDirection={'column'}
                            w={'200px'}
                            maxW={'200px'}
                            justifyContent={'center'}
                            alignItems={'center'}
                        >
                            <Image src={ROCKET_IMAGE} alt={'Rocket'} maxW={'120px'} mb={'20px'} />
                            <Text
                                textAlign={'center'}
                                maxW={'200px'}
                                fontSize={'sm'}
                                fontWeight={'semibold'}
                                lineHeight={'20px'}
                                color={'#0C0C0D'}
                                mb={'4px'}
                            >
                                {t('11229', 'Tell us what you think about employee page')}!
                            </Text>
                            <Text
                                textAlign={'center'}
                                maxW={'200px'}
                                fontSize={'xs'}
                                fontWeight={'normal'}
                                lineHeight={'18px'}
                                color={'#4B4B4E'}
                                mb={'20px'}
                            >
                                {t('11230', 'Your feedback will help make employee page even better')}
                            </Text>
                            <MainButton
                                title={t('9477', 'Submit feedback') || 'Submit feedback'}
                                view={'primary'}
                                size={'lg'}
                                onClick={() => {
                                    setIsOpen(false)
                                    open()
                                }}
                            />
                        </Flex>
                    </Box>,
                    document.body
                )}
        </Box>
    )
}
